// Custom MeshStandardMaterial for batched voxel rendering (P0.1-full + P3.3).
//
// Three.js's built-in `instanceColor` attribute multiplies with `diffuseColor`,
// but the renderer needs:
//   1. A LERP (not multiply) between the baked vertex color and the entity's
//      faction color, so a red-faction skeleton still reads as a skeleton.
//   2. A gold tint on selected entities.
//   3. A red flash when an entity takes damage.
//
// Faction colour (1) is applied per voxel via `aTeam`: team voxels (tabards,
// shields — BlockVoxel.team) take the owner's colour shaded by their own
// grey, other voxels keep their colours under a light 12% tint. Prefabs with
// no team voxels carry aTeam = -1 and keep the old 35% whole-body tint.
//
// Per-voxel material metadata (P3.3):
//   4. Per-vertex roughness overrides the material default.
//   5. Per-vertex metalness overrides the material default.
//   6. Per-vertex emissive accumulates into `totalEmissiveRadiance` so
//      individual voxels can glow (runes, crystals, torch flames, eyes).
//   8. A rim light and a grazing-edge darkening (VOXEL_RIM_STRENGTH,
//      VOXEL_EDGE_DARKEN) so silhouettes separate from the terrain.
//   7. A lighting floor: the lit result is blended toward the unlit albedo
//      (VOXEL_LIGHT_FLOOR). Three's physically based light units (r155+)
//      leave the scene's cosmetic day-night rig ~1/π as bright as it was
//      tuned for, which crushed units to dark silhouettes; props and
//      terrain went unlit (MeshBasicMaterial, #122) for the same reason.
//      Voxels keep directional shape from the sun but never go muddy.
//
// All per-entity quantities (1-3) are driven by instanced attributes
// (`aFactionColor`, `aSelected`, `aFlash`) attached to the InstancedMesh's
// geometry. Per-voxel quantities (4-6) are baked into the merged geometry
// as regular vertex attributes (`aRoughness`, `aMetalness`, `aEmissive`).
//
// See docs/superpowers/plans/2026-06-23-voxel-batching-p0_1-full.md.
import * as THREE from 'three';

let cachedMaterial: THREE.MeshStandardMaterial | null = null;

/** The material atlas: 4 x 4 tiles of 128 px, tile index = material class - 1 (`scripts/generate-unit-atlas.mjs`). */
export const ATLAS_URL = '/assets/textures/units/atlas.png';
/** World units one atlas tile covers: about 5.6 voxels, so grain stays finer than a body part. */
export const TEXTURE_WORLD_SIZE = 0.35;
/** How hard the atlas moves albedo and roughness (0 = off). */
export const TEXTURE_ALBEDO = 1.1;
export const TEXTURE_ROUGH = 0.6;

/** One neutral texel until the atlas loads: detail 0.5 changes nothing. */
const neutral = new THREE.DataTexture(new Uint8Array([128, 128, 128, 255]), 1, 1, THREE.RGBAFormat);
neutral.needsUpdate = true;
const atlasUniform: { value: THREE.Texture } = { value: neutral };
let atlasRequested = false;

/** Fetch the atlas once; units keep their grain-only look until it arrives (or if it never does). */
function loadAtlas(): void {
  if (atlasRequested || typeof document === 'undefined') return;
  atlasRequested = true;
  try {
    new THREE.TextureLoader().load(
      ATLAS_URL,
      (tex) => {
        tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
        tex.minFilter = THREE.LinearMipmapLinearFilter;
        tex.magFilter = THREE.LinearFilter;
        tex.generateMipmaps = true;
        tex.anisotropy = 4;
        // Detail maps are data, not colour: no sRGB decode.
        tex.colorSpace = THREE.NoColorSpace;
        tex.needsUpdate = true;
        atlasUniform.value = tex;
      },
      undefined,
      () => {},
    );
  } catch {
    // No texture loading here (a test, a headless build): the grain stands in.
  }
}

/**
 * How far the owner's colour bleeds over a unit's own palette. Identity lives in the
 * team voxels (tabards, shields, trim) and the ground ring; a strong whole-body tint
 * turned every army the colour of its player — an orc went teal under a blue player.
 */
export const FACTION_TINT_LEGACY = 0.1;
export const FACTION_TINT_BODY = 0.05;

/** Surface grain: albedo varies by ±GRAIN_ALBEDO and roughness by ±GRAIN_ROUGH around the authored value. */
export const GRAIN_ALBEDO = 0.3;
export const GRAIN_ROUGH = 0.25;

/** Share of unlit albedo mixed into the lit colour (0 = fully lit, 1 = unlit). */
export const VOXEL_LIGHT_FLOOR = 0.6;

/**
 * Rim light: a soft cool-white lift on surfaces turned away from the camera, so a
 * unit's silhouette separates from the terrain behind it (0 = off). Fresnel-shaped,
 * so it costs one dot product per fragment.
 */
export const VOXEL_RIM_STRENGTH = 0.14;

/**
 * Edge darkening: the grazing-angle band is darkened so the silhouette reads as a
 * thin dark line. A shader-only stand-in for an outline pass (no second render of
 * the units); 0 = off.
 */
export const VOXEL_EDGE_DARKEN = 0.35;

/** The GLSL injected before `opaque_fragment`: rim light, then edge darkening. */
export const VOXEL_RIM_GLSL = `float _ndv = clamp(dot(normalize(normal), normalize(vViewPosition)), 0.0, 1.0);
        float _grazing = 1.0 - _ndv;
        outgoingLight += vec3(0.92, 0.96, 1.0) * pow(_grazing, 3.0) * ${VOXEL_RIM_STRENGTH.toFixed(2)};
        outgoingLight *= 1.0 - ${VOXEL_EDGE_DARKEN.toFixed(2)} * pow(_grazing, 8.0);`;

/**
 * Returns a singleton voxel material with faction-tint + selection + flash
 * + per-voxel material metadata shader injection. Callers attach
 * `aFactionColor` / `aSelected` / `aFlash` InstancedBufferAttributes to the
 * geometry before drawing.
 */
export function getVoxelMaterial(): THREE.MeshStandardMaterial {
  if (cachedMaterial) return cachedMaterial;

  const mat = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.5,
    metalness: 0.1,
  });

  loadAtlas();
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uUnitAtlas = atlasUniform;
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
        attribute vec3 aFactionColor;
        attribute float aSelected;
        attribute float aFlash;
        attribute float aRoughness;
        attribute float aMetalness;
        attribute vec3 aEmissive;
        attribute float aTeam;
        attribute float aMat;
        varying float vTeam;
        varying float vMat;
        varying vec3 vModelPos;
        varying vec3 vModelNormal;
        varying vec3 vFactionColor;
        varying float vSelected;
        varying float vFlash;
        varying float vRoughness;
        varying float vMetalness;
        varying vec3 vEmissive;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vFactionColor = aFactionColor;
        vSelected = aSelected;
        vFlash = aFlash;
        vRoughness = aRoughness;
        vMetalness = aMetalness;
        vEmissive = aEmissive;
        vTeam = aTeam;
        vModelPos = transformed;
        vModelNormal = normal;
        vMat = aMat;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        varying vec3 vFactionColor;
        varying float vSelected;
        varying float vFlash;
        varying float vRoughness;
        varying float vMetalness;
        varying vec3 vEmissive;
        varying float vTeam;
        varying vec3 vModelPos;
        varying vec3 vModelNormal;
        varying float vMat;
        uniform sampler2D uUnitAtlas;
        // One atlas tile, sampled with the tile's own (unwrapped) derivatives so mips never seam.
        vec3 _atlasTile(vec2 uv, float cls) {
          float idx = cls - 1.0;
          vec2 cell = vec2(mod(idx, 4.0), floor(idx / 4.0));
          vec2 f = fract(uv);
          vec2 a = (cell + mix(vec2(0.0078), vec2(0.9922), f)) / 4.0;
          return textureGrad(uUnitAtlas, a, dFdx(uv) * 0.2461, dFdy(uv) * 0.2461).rgb;
        }
        // Triplanar in the unit's own space: (albedo detail, height, roughness detail).
        vec3 _unitTexture(vec3 p, vec3 n, float cls) {
          vec3 w = pow(abs(n), vec3(4.0));
          w /= (w.x + w.y + w.z + 0.0001);
          float s = ${(1 / TEXTURE_WORLD_SIZE).toFixed(4)};
          return w.x * _atlasTile(p.zy * s, cls) + w.y * _atlasTile(p.xz * s, cls) + w.z * _atlasTile(p.xy * s, cls);
        }
        // Cheap 3D value noise in the unit's own space: stable under instancing and animation.
        float _hash(vec3 p) {
          p = fract(p * 0.3183099 + 0.1);
          p *= 17.0;
          return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
        }
        float _vnoise(vec3 x) {
          vec3 i = floor(x);
          vec3 f = fract(x);
          f = f * f * (3.0 - 2.0 * f);
          return mix(
            mix(mix(_hash(i), _hash(i + vec3(1, 0, 0)), f.x), mix(_hash(i + vec3(0, 1, 0)), _hash(i + vec3(1, 1, 0)), f.x), f.y),
            mix(mix(_hash(i + vec3(0, 0, 1)), _hash(i + vec3(1, 0, 1)), f.x), mix(_hash(i + vec3(0, 1, 1)), _hash(i + vec3(1, 1, 1)), f.x), f.y),
            f.z);
        }`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        float _grain = _vnoise(vModelPos * 14.0) * 0.6 + _vnoise(vModelPos * 41.0) * 0.4 - 0.5;
        float _rough = 0.0;
        if (vMat > 0.5) {
          // A textured material: the atlas replaces the grain.
          vec3 _t = _unitTexture(vModelPos, normalize(vModelNormal), floor(vMat + 0.5));
          diffuseColor.rgb *= 1.0 + (_t.r - 0.5) * ${TEXTURE_ALBEDO.toFixed(2)};
          _rough = (_t.b - 0.5) * ${TEXTURE_ROUGH.toFixed(2)};
        } else {
          diffuseColor.rgb *= 1.0 + _grain * ${GRAIN_ALBEDO.toFixed(2)};
        }
        if (vTeam < -0.5) {
          diffuseColor.rgb = mix(diffuseColor.rgb, vFactionColor, ${FACTION_TINT_LEGACY.toFixed(2)});
        } else {
          vec3 _team = vFactionColor * (0.45 + 0.75 * diffuseColor.g);
          diffuseColor.rgb = mix(mix(diffuseColor.rgb, vFactionColor, ${FACTION_TINT_BODY.toFixed(2)}), _team, vTeam);
        }
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0, 0.85, 0.4), vSelected * 0.4);
        diffuseColor.rgb = mix(diffuseColor.rgb, vec3(1.0, 0.2, 0.2), vFlash);`,
      )
      .replace(
        '#include <roughness_fragment>',
        `#include <roughness_fragment>
        roughnessFactor = clamp(vRoughness + _rough + (vMat > 0.5 ? 0.0 : _grain * ${GRAIN_ROUGH.toFixed(2)}), 0.05, 1.0);`,
      )
      .replace(
        '#include <metalness_fragment>',
        `#include <metalness_fragment>
        metalnessFactor = vMetalness;`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        totalEmissiveRadiance += vEmissive;`,
      )
      .replace(
        '#include <opaque_fragment>',
        `outgoingLight = mix(outgoingLight, diffuseColor.rgb, ${VOXEL_LIGHT_FLOOR.toFixed(2)}) + totalEmissiveRadiance * ${VOXEL_LIGHT_FLOOR.toFixed(2)};
        ${VOXEL_RIM_GLSL}
        #include <opaque_fragment>`,
      );
  };

  cachedMaterial = mat;
  return mat;
}
